import {
  CommentRangeEnd,
  CommentRangeStart,
  CommentReference,
  Paragraph,
  TextRun,
  type ICommentOptions,
  type ParagraphChild,
} from 'docx';
import type { Comment, CommentReply } from './export-utils';
import { stripHtml } from './note-utils';

export type WordCommentPlacement = {
  id: number;
  start: number;
  end: number;
};

export type TextSegmentForCommentInjection = {
  text: string;
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
};

export type PendingNativeWordComment = {
  id: number;
  selectedText: string;
};

export const authorFromEmail = (userEmail: string | undefined): string => {
  if (userEmail && userEmail.trim().length > 0) {
    return userEmail.trim();
  }
  return 'Anonymous';
};

export const authorFromComment = (comment: Comment): string => {
  return authorFromEmail(comment.userEmail);
};

export const initialsFromAuthor = (author: string): string => {
  const local = author.includes('@') ? author.split('@')[0] : author;
  const parts = local.split(/[.\s_-]+/).filter((part) => part.length > 0);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || 'AN';
};

export const buildCommentBodyParagraphs = (
  content: string,
  replies: CommentReply[] | undefined,
  replyLabel: string,
): Paragraph[] => {
  const children: Paragraph[] = [
    new Paragraph({
      children: [new TextRun({ text: stripHtml(content) })],
    }),
  ];

  for (const reply of replies ?? []) {
    const replyAuthor = authorFromEmail(reply.userEmail);
    children.push(
      new Paragraph({
        spacing: { before: 120 },
        children: [
          new TextRun({ text: `${replyLabel} · ${replyAuthor}`, italics: true, size: 18 }),
        ],
      }),
      new Paragraph({
        children: [new TextRun({ text: stripHtml(reply.comment) })],
      }),
    );
  }

  return children;
};

export const buildNativeWordCommentDefinitions = (
  comments: Comment[],
  replyLabel: string = 'Reply',
): ICommentOptions[] => {
  return comments.map((comment, index) => {
    const author = authorFromComment(comment);
    return {
      id: index,
      author,
      initials: initialsFromAuthor(author),
      date: new Date(),
      children: buildCommentBodyParagraphs(comment.content, comment.replies, replyLabel),
    };
  });
};

export const findCommentPlacementsInText = (
  plainText: string,
  pendingComments: PendingNativeWordComment[],
): { placements: WordCommentPlacement[]; remaining: PendingNativeWordComment[] } => {
  const placements: WordCommentPlacement[] = [];
  const remaining: PendingNativeWordComment[] = [];
  const occupied: Array<{ start: number; end: number }> = [];

  const overlapsOccupied = (start: number, end: number): boolean => {
    return occupied.some((range) => start < range.end && end > range.start);
  };

  for (const pending of pendingComments) {
    const needle = pending.selectedText;
    if (!needle) {
      remaining.push(pending);
      continue;
    }

    let foundStart = -1;
    let cursor = 0;
    while (cursor <= plainText.length) {
      const index = plainText.indexOf(needle, cursor);
      if (index < 0) {
        break;
      }
      const end = index + needle.length;
      if (!overlapsOccupied(index, end)) {
        foundStart = index;
        occupied.push({ start: index, end });
        placements.push({ id: pending.id, start: index, end });
        break;
      }
      cursor = index + 1;
    }

    if (foundStart < 0) {
      remaining.push(pending);
    }
  }

  return { placements, remaining };
};

export const injectCommentRangesIntoTextSegments = (
  segments: TextSegmentForCommentInjection[],
  pendingComments: PendingNativeWordComment[],
): { children: ParagraphChild[]; remaining: PendingNativeWordComment[] } => {
  const plainText = segments.map((segment) => segment.text).join('');
  const { placements, remaining } = findCommentPlacementsInText(plainText, pendingComments);

  if (placements.length === 0) {
    return {
      children: segments.map(
        (segment) =>
          new TextRun({
            text: segment.text,
            bold: segment.bold,
            italics: segment.italics,
            underline: segment.underline ? {} : undefined,
          }),
      ),
      remaining,
    };
  }

  type Marker =
    | { kind: 'start'; id: number; offset: number }
    | { kind: 'end'; id: number; offset: number };

  const markers: Marker[] = [];
  for (const placement of placements) {
    markers.push({ kind: 'start', id: placement.id, offset: placement.start });
    markers.push({ kind: 'end', id: placement.id, offset: placement.end });
  }

  markers.sort((a, b) => {
    if (a.offset !== b.offset) {
      return a.offset - b.offset;
    }
    if (a.kind === b.kind) {
      return a.kind === 'start' ? a.id - b.id : b.id - a.id;
    }
    return a.kind === 'end' ? -1 : 1;
  });

  const children: ParagraphChild[] = [];
  let markerIndex = 0;
  let globalOffset = 0;

  const flushMarkersAt = (offset: number): void => {
    while (markerIndex < markers.length && markers[markerIndex].offset === offset) {
      const marker = markers[markerIndex];
      if (marker.kind === 'start') {
        children.push(new CommentRangeStart(marker.id));
      } else {
        children.push(new CommentRangeEnd(marker.id));
        children.push(
          new TextRun({
            children: [new CommentReference(marker.id)],
          }),
        );
      }
      markerIndex += 1;
    }
  };

  for (const segment of segments) {
    let localOffset = 0;
    while (localOffset < segment.text.length) {
      flushMarkersAt(globalOffset + localOffset);

      const nextMarkerOffset =
        markerIndex < markers.length ? markers[markerIndex].offset : Number.POSITIVE_INFINITY;
      const absoluteEnd = Math.min(globalOffset + segment.text.length, nextMarkerOffset);
      const sliceEnd = absoluteEnd - globalOffset;

      if (sliceEnd > localOffset) {
        const text = segment.text.slice(localOffset, sliceEnd);
        children.push(
          new TextRun({
            text,
            bold: segment.bold,
            italics: segment.italics,
            underline: segment.underline ? {} : undefined,
          }),
        );
        localOffset = sliceEnd;
      } else if (markerIndex < markers.length && markers[markerIndex].offset === globalOffset + localOffset) {
        continue;
      } else {
        break;
      }
    }
    globalOffset += segment.text.length;
  }

  flushMarkersAt(globalOffset);

  return { children, remaining };
};

export const attachPointCommentsToChildren = (
  children: ParagraphChild[],
  pendingComments: PendingNativeWordComment[],
): ParagraphChild[] => {
  if (pendingComments.length === 0) {
    return children;
  }

  const pointMarkers: ParagraphChild[] = [];
  for (const pending of pendingComments) {
    pointMarkers.push(new CommentRangeStart(pending.id));
    pointMarkers.push(new CommentRangeEnd(pending.id));
    pointMarkers.push(
      new TextRun({
        children: [new CommentReference(pending.id)],
      }),
    );
  }

  return [...children, ...pointMarkers];
};
